/**
 * features/media/siteImagesWrite.ts
 * ------------------------------------------------------------------
 * Writes for the public site's image library.
 *
 * The file goes to Storage under `site/<slot>.<ext>`; the pointer goes
 * to the single `siteContent/images` document. One document rather
 * than one per image so the public page resolves every slot in a
 * single read.
 *
 * Uploading to a slot that already holds an image overwrites the
 * Storage object at the same path, so the library never accumulates
 * orphans.
 * ------------------------------------------------------------------
 */
import { deleteField, doc, getDoc } from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, setDoc, storage, updateDoc } from '@/lib/firebase';
import { logAudit } from '@/lib/audit';
import { resizeToSlot } from '@/lib/imageResize';
import {
  COLLECTIONS,
  SITE_IMAGES_DOC_ID,
  type SiteImage,
  type SiteImageMap,
} from '@/types';
import type { ImageSlot } from '@/features/media/slots';

function imagesDoc() {
  return doc(db, COLLECTIONS.siteContent, SITE_IMAGES_DOC_ID);
}

function extFor(contentType: string): string {
  return contentType === 'image/webp' ? 'webp' : 'jpg';
}

export interface Actor {
  uid: string;
  email: string;
}

/**
 * Resize `file` to the slot's exact dimensions, upload it, and record
 * the pointer. Returns the stored record (including a preview-ready
 * download URL).
 */
export async function uploadSlotImage(
  slot: ImageSlot,
  file: File,
  actor: Actor,
): Promise<SiteImage> {
  const resized = await resizeToSlot(file, slot.width, slot.height);
  const path = `site/${slot.id}.${extFor(resized.contentType)}`;

  const objectRef = storageRef(storage, path);
  await uploadBytes(objectRef, resized.blob, {
    contentType: resized.contentType,
    cacheControl: 'public, max-age=3600',
  });
  const url = await getDownloadURL(objectRef);

  const record: SiteImage = {
    url,
    path,
    width: resized.width,
    height: resized.height,
    contentType: resized.contentType,
    bytes: resized.bytes,
    originalName: file.name,
    updatedAt: new Date().toISOString(),
    updatedBy: actor.email,
  };

  // Merge so each slot is independent — two admins uploading different
  // slots can't clobber one another.
  await setDoc(imagesDoc(), { [slot.id]: record }, { merge: true });

  await logAudit({
    actorUid: actor.uid,
    actorEmail: actor.email,
    action: 'siteImage.upload',
    entityType: 'siteImage',
    entityId: slot.id,
    meta: {
      slot: slot.label,
      originalName: file.name,
      bytes: resized.bytes,
      size: `${resized.width}x${resized.height}`,
    },
  });

  return record;
}

/** Remove a slot's image from Storage and drop its pointer. */
export async function removeSlotImage(
  slotId: string,
  actor: Actor,
): Promise<void> {
  const snap = await getDoc(imagesDoc());
  const current = (snap.data() as SiteImageMap | undefined)?.[slotId];

  if (current?.path) {
    try {
      await deleteObject(storageRef(storage, current.path));
    } catch (err) {
      // A missing object shouldn't block clearing the pointer — the
      // user's intent is "stop showing this image".
      console.warn('[siteImages] could not delete stored object', current.path, err);
    }
  }

  await updateDoc(imagesDoc(), { [slotId]: deleteField() });

  await logAudit({
    actorUid: actor.uid,
    actorEmail: actor.email,
    action: 'siteImage.remove',
    entityType: 'siteImage',
    entityId: slotId,
    before: current ? { path: current.path, originalName: current.originalName } : undefined,
  });
}
